// The Living Atlas mode registry: the spec's 15 experiences plus Storm Center
// (16), Planet Events (17) and Webcams (18), always listed. A mode
// without a lawful, working free source stays here with an honest status and
// the route to supporting it — it is never dropped or faked.

import type { AtlasLayerDescriptor, AtlasModeId } from "./types";

export type LocationPresetId = "home" | "colorado" | "us" | "world";

/**
 * What the mode can show today.
 * - live: real data from verified providers.
 * - setup: built, but needs something only the publisher or household can provide (a key, a place, hardware).
 * - planned: not built yet; the reason and route are shown.
 */
export type ModeAvailability = "live" | "setup" | "planned";

/** How time works in this mode, which decides what the time controller offers. */
export type TemporalKind = "frames" | "replay" | "snapshot" | "calculated" | "none";

export type AtlasIconName =
  | "globe"
  | "satellite"
  | "plane"
  | "flame"
  | "pulse"
  | "air"
  | "aurora"
  | "orbit"
  | "river"
  | "mountain"
  | "bolt"
  | "bus"
  | "road"
  | "wave"
  | "night"
  | "wind"
  | "storm"
  | "camera";

export interface AtlasMode {
  id: AtlasModeId;
  name: string;
  /** One line under the name in the switcher. */
  tagline: string;
  /** What the mode shows and how to read it. */
  description: string;
  icon: AtlasIconName;
  /** Accent used for the mode's chrome (legends, pulses). */
  hue: string;
  phase: 1 | 2 | 3;
  availability: ModeAvailability;
  temporal: TemporalKind;
  providers: string[];
  layers: AtlasLayerDescriptor[];
  defaultPreset: LocationPresetId;
  /** Overrides the preset's camera for the first visit (e.g. polar view for aurora). */
  defaultView?: { lat: number; lon: number; zoom: number };
  projection: "mercator" | "globe";
  /** Always shown in the details of the mode: the honest limits of the data. */
  caveat: string;
  /** For modes that aren't live: why, and what it would take. */
  unavailable?: { reason: "not-built" | "needs-key" | "needs-hardware" | "licensing" | "outside-coverage"; title: string; detail: string; route: string };
  /** Informational-only reminder for hazard maps. */
  safety?: string;
}

const NOT_EMERGENCY = "Informational only — not an emergency warning system. Follow official alerts and local authorities.";

export const ATLAS_MODES: AtlasMode[] = [
  {
    id: "earthpulse",
    name: "EarthPulse",
    tagline: "Earthquakes as they’re detected",
    description: "Recent earthquakes from the USGS real-time feeds. Circles grow with magnitude and are colored by depth; new events pulse once. Replay the window to watch them appear.",
    icon: "pulse",
    hue: "#f0b44d",
    phase: 1,
    availability: "live",
    temporal: "replay",
    providers: ["usgs-quakes"],
    layers: [
      { id: "quakes", label: "Earthquakes", kind: "geojson", defaultVisible: true, opacity: 0.9, attribution: "U.S. Geological Survey", note: "Size = magnitude, color = depth" },
      { id: "plates", label: "Faint plate glow", kind: "computed", defaultVisible: false, opacity: 0.5, attribution: "Kinmantle", note: "Heat of the past window’s activity" },
    ],
    defaultPreset: "world",
    projection: "mercator",
    caveat: "Event time is when the quake happened; USGS may revise magnitude and location for hours afterwards. Automatic solutions are marked as such.",
    safety: NOT_EMERGENCY,
  },
  {
    id: "planet-events",
    name: "Planet Events",
    tagline: "Storms, volcanoes, ice and big fires",
    description: "Open natural events curated by NASA EONET — severe storms with their tracks, erupting volcanoes, icebergs, large wildfires, dust and floods — each at its latest observation, plus the current USGS alert level and aviation color code for every U.S. volcano.",
    icon: "globe",
    hue: "#e8a25c",
    phase: 3,
    availability: "live",
    temporal: "snapshot",
    providers: ["nasa-eonet", "usgs-volcanoes"],
    layers: [
      { id: "events", label: "Natural events (EONET)", kind: "geojson", defaultVisible: true, opacity: 1, attribution: "NASA EONET", note: "Latest observation of each open event" },
      { id: "tracks", label: "Storm tracks", kind: "geojson", defaultVisible: true, opacity: 0.8, attribution: "NASA EONET", note: "Observed positions, oldest to newest" },
      { id: "volcanoes", label: "U.S. volcano alert levels", kind: "geojson", defaultVisible: true, opacity: 1, attribution: "USGS Volcano Hazards Program", note: "Color = aviation color code" },
    ],
    defaultPreset: "world",
    projection: "mercator",
    caveat: "EONET is curated metadata for general information: positions and dates come from each event’s source and are often approximate, and an event may stay “open” after it has ended. Wildfires are listed from 5,000 acres up. USGS alert levels cover U.S. volcanoes only.",
    safety: NOT_EMERGENCY,
  },
  {
    id: "webcams",
    name: "Webcams",
    tagline: "Tap a camera and watch",
    description: "Every Lookout camera on the map — near home, at your mountains and places, and hand-picked live streams around the world — plus, as you zoom in, Windy’s webcams for wherever you’re looking. Tap one for its picture, then watch it big.",
    icon: "camera",
    hue: "#ff7a6b",
    phase: 3,
    availability: "live",
    temporal: "snapshot",
    providers: ["windy-webcams", "youtube-live"],
    layers: [
      { id: "live", label: "Live video", kind: "geojson", defaultVisible: true, opacity: 1, attribution: "YouTube (each stream credited)", note: "Hand-picked 24/7 streams" },
      { id: "stills", label: "Webcams", kind: "geojson", defaultVisible: true, opacity: 1, attribution: "Windy.com", note: "A still every few minutes, with today’s timelapse" },
    ],
    defaultPreset: "world",
    projection: "mercator",
    caveat: "Most webcams send a still every few minutes rather than video; “Live” marks real live streams. Cameras go offline and streams end without notice. A pin is where the camera stands, not what it’s pointed at.",
  },
  {
    id: "aurora-live",
    name: "Aurora Live",
    tagline: "The auroral oval, right now",
    description: "NOAA SWPC’s OVATION model of where aurora is likely in the next 30–90 minutes, with the planetary Kp index and what it means from home.",
    icon: "aurora",
    hue: "#5fe3a1",
    phase: 1,
    availability: "live",
    temporal: "snapshot",
    providers: ["noaa-swpc-ovation", "noaa-swpc"],
    layers: [
      { id: "oval", label: "Aurora probability", kind: "raster", defaultVisible: true, opacity: 0.85, attribution: "NOAA SWPC OVATION", note: "Short-term model forecast" },
      { id: "night", label: "Night side", kind: "computed", defaultVisible: true, opacity: 0.45, attribution: "Calculated", note: "Aurora is only visible in darkness" },
    ],
    defaultPreset: "world",
    defaultView: { lat: 58, lon: -100, zoom: 1.6 },
    projection: "globe",
    caveat: "A probability from a model, not a sighting. Clouds, moonlight and city lights decide what you actually see.",
  },
  {
    id: "live-earth",
    name: "Live Earth",
    tagline: "Satellite views of the planet",
    description: "GOES-East and GOES-West imagery through NASA GIBS: GeoColor by day and night, clean infrared for cloud tops, plus the existing NWS radar as an overlay.",
    icon: "satellite",
    hue: "#7cc4ff",
    phase: 1,
    availability: "live",
    temporal: "frames",
    providers: ["nasa-gibs", "noaa-radar"],
    layers: [
      { id: "geocolor-east", label: "GOES-East GeoColor", kind: "raster", defaultVisible: true, opacity: 1, attribution: "NOAA GOES via NASA GIBS", note: "Americas & Atlantic, 10-min frames" },
      { id: "geocolor-west", label: "GOES-West GeoColor", kind: "raster", defaultVisible: false, opacity: 1, attribution: "NOAA GOES via NASA GIBS", note: "Pacific & western U.S." },
      { id: "ir-east", label: "Clean infrared (GOES-East)", kind: "raster", defaultVisible: false, opacity: 0.85, attribution: "NOAA GOES via NASA GIBS", note: "Cloud-top temperature, day and night" },
      { id: "radar", label: "NWS radar (latest)", kind: "raster", defaultVisible: false, opacity: 0.75, attribution: "NOAA/NWS MRMS", note: "Same feed as the Weather screen" },
    ],
    defaultPreset: "us",
    projection: "mercator",
    caveat: "Near-real-time, not continuous: GIBS publishes GOES frames roughly every 10 minutes, usually 30–60 minutes behind.",
  },
  {
    id: "firewatch",
    name: "Wildfires",
    tagline: "Fire detections and incidents",
    description: "Satellite hot-spot detections from NASA FIRMS (VIIRS) colored by age, reported wildfire incidents from the national interagency WFIGS service, and the smoke NOAA analysts traced on satellite images today.",
    icon: "flame",
    hue: "#ff7a45",
    phase: 1,
    availability: "live",
    temporal: "replay",
    providers: ["nasa-firms", "nifc-wfigs", "noaa-hms"],
    layers: [
      { id: "detections", label: "Satellite detections (VIIRS)", kind: "geojson", defaultVisible: true, opacity: 0.95, attribution: "NASA FIRMS", note: "Color = hours since the overpass" },
      { id: "incidents", label: "Reported incidents", kind: "geojson", defaultVisible: true, opacity: 1, attribution: "NIFC WFIGS", note: "Wildfires; prescribed burns shown faint" },
      { id: "smoke", label: "Smoke (NOAA HMS)", kind: "geojson", defaultVisible: true, opacity: 0.85, attribution: "NOAA NESDIS HMS", note: "Satellite analysis, not ground air quality" },
      { id: "home-ring", label: "Distance rings from home", kind: "computed", defaultVisible: true, opacity: 0.6, attribution: "Calculated" },
    ],
    defaultPreset: "us",
    projection: "mercator",
    caveat: "Detections are hot pixels seen from orbit (375 m), not fire perimeters; some are industrial heat or flares, and clouds hide fires. Overpasses are a few times a day. Smoke is a hand-drawn analysis of daytime satellite images: smoke anywhere in the air column, not ground-level air quality, and smoke under cloud is missed.",
    safety: NOT_EMERGENCY,
  },
  {
    id: "earth-at-night",
    name: "Earth at Night",
    tagline: "City lights and the moving terminator",
    description: "NASA’s Black Marble city-lights composite with the real day/night line, twilight bands and the point where the sun is overhead, all calculated for this moment.",
    icon: "night",
    hue: "#f7c56a",
    phase: 1,
    availability: "live",
    temporal: "calculated",
    providers: ["nasa-gibs"],
    layers: [
      { id: "lights", label: "City lights (2016 composite)", kind: "raster", defaultVisible: true, opacity: 1, attribution: "NASA Black Marble via GIBS", note: "Historic annual composite, not live" },
      { id: "terminator", label: "Night & twilight", kind: "computed", defaultVisible: true, opacity: 0.55, attribution: "Calculated" },
      { id: "sun", label: "Sun overhead point", kind: "computed", defaultVisible: true, opacity: 1, attribution: "Calculated" },
    ],
    defaultPreset: "world",
    projection: "mercator",
    caveat: "The lights are a cloud-free 2016 annual composite — they don’t show today’s power use or outages. The terminator is calculated, not observed.",
  },
  {
    id: "riverpulse",
    name: "WaterPulse",
    tagline: "Rivers, streams and reservoirs",
    description: "Live streamflow at USGS gauges across your region, and how full the lakes and reservoirs are. Gauge circles grow with flow and are colored by how it compares with yesterday’s average; reservoirs show the water they hold and whether they’re filling or draining.",
    icon: "river",
    hue: "#4fb3e8",
    phase: 2,
    availability: "live",
    temporal: "snapshot",
    providers: ["usgs-water", "co-dwr"],
    layers: [
      { id: "rivers", label: "Rivers & creeks", kind: "vector", defaultVisible: true, opacity: 1, attribution: "USGS National Hydrography Dataset · © OpenStreetMap contributors", note: "Creeks appear as you zoom in" },
      { id: "gauges", label: "Stream gauges", kind: "geojson", defaultVisible: true, opacity: 1, attribution: "U.S. Geological Survey", note: "Size = flow, color = trend vs. yesterday" },
      { id: "reservoirs", label: "Reservoirs", kind: "geojson", defaultVisible: true, opacity: 1, attribution: "Colorado Division of Water Resources", note: "Size = water held, color = filling or draining" },
      { id: "labels", label: "Gauge names", kind: "vector", defaultVisible: true, opacity: 1, attribution: "U.S. Geological Survey", note: "When zoomed in" },
    ],
    defaultPreset: "colorado",
    projection: "mercator",
    caveat: "Provisional data, subject to revision. Flood stages aren’t published through this service, so gauges show trend, not flood status. Ice and maintenance can make readings wrong. Reservoir capacity isn’t published with these readings, so they show water held, not percent full.",
    safety: NOT_EMERGENCY,
  },
  {
    id: "mountain-conditions",
    name: "Mountain Conditions",
    tagline: "Snowpack across the high country",
    description: "SNOTEL stations from USDA NRCS: snow-water equivalent compared with the station’s median for today’s date, snow depth and the change since yesterday — over NOAA’s daily national snow analysis of depth or water content.",
    icon: "mountain",
    hue: "#cfe7ff",
    phase: 2,
    availability: "live",
    temporal: "snapshot",
    providers: ["nrcs-awdb", "noaa-nohrsc"],
    layers: [
      { id: "resorts", label: "Our mountains", kind: "geojson", defaultVisible: true, opacity: 1, attribution: "USDA NRCS SNOTEL · NWS · CDOT COtrip", note: "Your ski areas (Admin → Outdoors): new snow and depth" },
      { id: "snotel", label: "SNOTEL stations", kind: "geojson", defaultVisible: true, opacity: 1, attribution: "USDA NRCS", note: "Color = % of median snow-water" },
      { id: "snow-analysis", label: "Snow cover (NOAA analysis)", kind: "raster", defaultVisible: true, opacity: 0.7, attribution: "NOAA NWS NOHRSC", note: "Daily snow depth or water, contiguous U.S." },
    ],
    defaultPreset: "colorado",
    projection: "mercator",
    caveat: "Snow-water equivalent is the water in the snowpack, not its depth. Each station measures one spot; early in the season a percent of median swings wildly, so it’s hidden until the median is meaningful. The NOAA snow cover is a daily modelled analysis (about 1 km) for the contiguous U.S. only, with its analysis time shown.",
  },
  {
    id: "invisible-air",
    name: "Invisible Air",
    tagline: "Air quality at monitoring stations",
    description: "The latest hourly AQI at every reporting monitor across North America, from EPA AirNow, colored with the official AQI categories. The pollutant setting each value is shown on tap.",
    icon: "air",
    hue: "#a3d977",
    phase: 2,
    availability: "live",
    temporal: "snapshot",
    providers: ["epa-airnow-gis"],
    layers: [
      { id: "stations", label: "Monitoring stations", kind: "geojson", defaultVisible: true, opacity: 1, attribution: "U.S. EPA AirNow", note: "Color = AQI category" },
      { id: "temporary", label: "Temporary smoke monitors", kind: "geojson", defaultVisible: true, opacity: 1, attribution: "U.S. EPA AirNow", note: "Fire-season monitors, shown with a ring" },
    ],
    defaultPreset: "us",
    projection: "mercator",
    caveat: "Readings are at stations, not a continuous field, and are preliminary — not yet verified by EPA. Between stations the air may differ.",
  },
  {
    id: "roadwatch",
    name: "RoadWatch",
    tagline: "Closures, incidents and conditions",
    description: "Road closures, incidents and conditions for Colorado corridors.",
    icon: "road",
    hue: "#ffd166",
    phase: 2,
    availability: "planned",
    temporal: "snapshot",
    providers: ["cdot"],
    layers: [{ id: "events", label: "Incidents & closures", kind: "geojson", defaultVisible: true, opacity: 1, attribution: "CDOT" }],
    defaultPreset: "colorado",
    projection: "mercator",
    caveat: "Only what the agency publishes; camera images are shown only where reuse is permitted.",
    safety: NOT_EMERGENCY,
    unavailable: {
      reason: "licensing",
      title: "Needs CDOT data access",
      detail: "COtrip’s public website is not a feed we may reuse. CDOT offers data to registered developers; that registration and its terms need review before anything is shown here.",
      route: "Publisher registers for CDOT’s developer data, reviews terms, and adds the key as a Worker secret.",
    },
  },
  {
    id: "windflow",
    name: "WindFlow",
    tagline: "Wind streamlines from the forecast model",
    description: "Animated wind at the surface and aloft from NOAA’s GFS model, with the model run time.",
    icon: "wind",
    hue: "#9fd3ff",
    phase: 2,
    availability: "planned",
    temporal: "frames",
    providers: ["noaa-nomads"],
    layers: [{ id: "wind", label: "Wind particles", kind: "particles", defaultVisible: true, opacity: 0.8, attribution: "NOAA NCEP GFS" }],
    defaultPreset: "us",
    projection: "mercator",
    caveat: "A forecast model, not measured wind.",
    unavailable: {
      reason: "not-built",
      title: "Needs a preprocessing step",
      detail: "GFS winds come as GRIB2 files that are too heavy to parse in a browser on a Pi. They must be turned into small wind textures off-device first.",
      route: "Add a scheduled job (local agent or Worker cron) that converts the GFS 10 m / 850 hPa U/V fields to a compact PNG grid.",
    },
  },
  {
    id: "orbit",
    name: "Orbit",
    tagline: "The space station, now and overhead",
    description: "Where the International Space Station is now and where it goes next: its ground track, the area that can see it, and the naked-eye passes coming up over home — all calculated in the browser from CelesTrak’s latest orbital elements.",
    icon: "orbit",
    hue: "#c3a6ff",
    phase: 3,
    availability: "live",
    temporal: "calculated",
    providers: ["celestrak"],
    layers: [
      { id: "iss", label: "ISS position", kind: "computed", defaultVisible: true, opacity: 1, attribution: "Calculated from CelesTrak elements" },
      { id: "tracks", label: "Ground track", kind: "computed", defaultVisible: true, opacity: 0.85, attribution: "Calculated from CelesTrak elements", note: "Last 45 min and next 90 min" },
      { id: "footprint", label: "Visible-from area", kind: "computed", defaultVisible: true, opacity: 0.5, attribution: "Calculated", note: "Where the ISS is above the horizon" },
      { id: "night", label: "Night side", kind: "computed", defaultVisible: true, opacity: 0.45, attribution: "Calculated" },
    ],
    defaultPreset: "world",
    projection: "globe",
    caveat: "Positions are calculated (SGP4) from an orbital element set that ages; accuracy drops after a few days and after the station reboosts. Passes assume a clear sky.",
  },
  {
    id: "airspace-live",
    name: "Airspace Live",
    tagline: "Aircraft overhead",
    description: "Aircraft positions, callsigns and altitude from a local ADS-B receiver.",
    icon: "plane",
    hue: "#e9e3d5",
    phase: 3,
    availability: "planned",
    temporal: "snapshot",
    providers: ["local-adsb"],
    layers: [{ id: "aircraft", label: "Aircraft", kind: "geojson", defaultVisible: true, opacity: 1, attribution: "Local receiver" }],
    defaultPreset: "home",
    projection: "mercator",
    caveat: "Only aircraft that broadcast ADS-B and are within range of the receiver.",
    unavailable: {
      reason: "needs-hardware",
      title: "Needs a local ADS-B receiver",
      detail: "Free public flight APIs restrict commercial use. The lawful free route is a USB ADS-B receiver at home feeding the Kinmantle local agent.",
      route: "Plug in an RTL-SDR ADS-B dongle running dump1090/readsb; the local agent will relay positions over the LAN.",
    },
  },
  {
    id: "cityflow",
    name: "CityFlow",
    tagline: "Transit vehicles in motion",
    description: "Buses and trains from your agency’s GTFS-Realtime feed, with stop predictions and service alerts.",
    icon: "bus",
    hue: "#7ee0c3",
    phase: 3,
    availability: "planned",
    temporal: "snapshot",
    providers: ["gtfs-rt"],
    layers: [{ id: "vehicles", label: "Vehicles", kind: "geojson", defaultVisible: true, opacity: 1, attribution: "Transit agency" }],
    defaultPreset: "home",
    projection: "mercator",
    caveat: "Coverage and redistribution terms differ per agency.",
    unavailable: {
      reason: "not-built",
      title: "Per-agency setup",
      detail: "Each transit agency publishes its own GTFS and GTFS-Realtime feeds under its own terms (Denver’s RTD among them).",
      route: "Pick an agency, review its developer terms, then add its feed URLs in Admin → Living Atlas.",
    },
  },
  {
    id: "ocean-alive",
    name: "Ocean Alive",
    tagline: "Sea-surface temperature",
    description: "The daily GHRSST MUR sea-surface temperature analysis through NASA GIBS, with its analysis date and a temperature legend. Tap the ocean to read the analysed temperature there.",
    icon: "wave",
    hue: "#3fb6c9",
    phase: 3,
    availability: "live",
    temporal: "snapshot",
    providers: ["nasa-gibs"],
    layers: [{ id: "sst", label: "Sea-surface temperature", kind: "raster", defaultVisible: true, opacity: 0.9, attribution: "GHRSST MUR via NASA GIBS", note: "Daily analysis, about a day behind" }],
    defaultPreset: "world",
    projection: "globe",
    caveat: "A daily analysis that blends several satellites with buoy and ship readings — not an instantaneous image. Ocean currents aren’t shown: no free, current, commercially usable currents source has been verified (GIBS’s OSCAR currents end in mid-2024).",
  },
  {
    id: "lightning-live",
    name: "Lightning Live",
    tagline: "Lightning, where it’s licensed",
    description: "Lightning activity from the GOES Geostationary Lightning Mapper or a local sensor.",
    icon: "bolt",
    hue: "#fff07a",
    phase: 3,
    availability: "planned",
    temporal: "frames",
    providers: ["goes-glm"],
    layers: [{ id: "flashes", label: "Lightning", kind: "raster", defaultVisible: true, opacity: 1, attribution: "NOAA" }],
    defaultPreset: "us",
    projection: "mercator",
    caveat: "There is no free nationwide strike-by-strike feed; strikes are never invented to fill gaps.",
    unavailable: {
      reason: "not-built",
      title: "No free strike feed",
      detail: "Commercial lightning networks are paid. GOES GLM flash-density imagery is public but must be verified as a tile service; a local AS3935 sensor can report nearby strikes.",
      route: "Verify a public GLM imagery service, or add an optional local lightning sensor to the local agent.",
    },
  },
  {
    id: "storm-center",
    name: "Storm Center",
    tagline: "Warnings, storm reports and hurricanes",
    description: "Active National Weather Service warnings that have their own storm-based polygon, today’s tornado, hail and wind reports from the Storm Prediction Center, and National Hurricane Center forecast cones and tracks whenever a tropical cyclone is active.",
    icon: "storm",
    hue: "#ff6b6b",
    phase: 3,
    availability: "live",
    temporal: "snapshot",
    providers: ["nws-national", "spc-reports", "nhc-gis"],
    layers: [
      { id: "warnings", label: "NWS warnings (storm-based)", kind: "geojson", defaultVisible: true, opacity: 0.8, attribution: "NOAA/NWS", note: "Polygons NWS drew for this storm" },
      { id: "reports", label: "Storm reports (SPC)", kind: "geojson", defaultVisible: true, opacity: 1, attribution: "NOAA/NWS SPC", note: "Preliminary tornado, hail and wind reports" },
      { id: "tropical", label: "Hurricane cones & tracks (NHC)", kind: "geojson", defaultVisible: true, opacity: 0.8, attribution: "NOAA/NWS NHC", note: "Only while a storm is active" },
    ],
    defaultPreset: "us",
    projection: "mercator",
    caveat: "Only warnings issued with their own polygon are drawn; alerts issued for whole counties or zones (winter storms, heat, wind) are counted, not mapped. Storm reports are preliminary and unverified. The cone shows where the storm’s center will probably go, not the size of its impacts.",
    safety: NOT_EMERGENCY,
  },
];

export const ATLAS_MODE_IDS = ATLAS_MODES.map((m) => m.id);

const BY_ID = new Map(ATLAS_MODES.map((m) => [m.id, m]));

export function atlasMode(id: string): AtlasMode | undefined {
  return BY_ID.get(id as AtlasModeId);
}

export function isAtlasModeId(id: unknown): id is AtlasModeId {
  return typeof id === "string" && BY_ID.has(id as AtlasModeId);
}

export interface LocationPreset {
  id: string;
  name: string;
  lat: number;
  lon: number;
  zoom: number;
}

export const FIXED_PRESETS: Record<Exclude<LocationPresetId, "home">, LocationPreset> = {
  colorado: { id: "colorado", name: "Colorado", lat: 39.0, lon: -105.55, zoom: 6.2 },
  us: { id: "us", name: "United States", lat: 38.5, lon: -97.5, zoom: 3.3 },
  world: { id: "world", name: "World", lat: 22, lon: -40, zoom: 1.4 },
};
